import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ActionSpace } from './AnswerActions.style'
import { faClone, faComment, faExclamationTriangle, faThumbsUp, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import Timeago from '../common/Timeago'
import { MDBBadge, MDBDropdown, MDBDropdownItem, MDBDropdownLink, MDBDropdownMenu } from 'mdb-react-ui-kit'
import { Button, DropdownToggle } from '../App.styles'
import { LinkButton, Spinner } from '../common'
import { useParams } from 'react-router-dom'

const ImagesDropdown = ({ photo = '', sticker, type }) => {
    const { t } = useTranslation()
    const photoTypes = [1, 9]
    const watermarkedFree = photo.replace(/-wm/, '')
    const shouldRender = (photo || sticker || watermarkedFree) && photoTypes.includes(parseInt(type))
    
    return shouldRender
        ? (
            <MDBDropdown>
                <DropdownToggle size="sm">
                    {t(`Images`)}
                </DropdownToggle>
                <MDBDropdownMenu>
                    {
                        photo
                            ? (
                                <MDBDropdownItem>
                                    <MDBDropdownLink className="fa--medium" href={photo} target="_blank">
                                        {t(`Watermarked`)} <FontAwesomeIcon icon={faExternalLinkAlt} />
                                    </MDBDropdownLink>
                                </MDBDropdownItem>
                            )
                            : null
                    }
                    {
                        watermarkedFree
                            ? (
                                <MDBDropdownItem>
                                    <MDBDropdownLink className="fa--medium" href={watermarkedFree} target="_blank">
                                        {t(`Watermark-free`)} <FontAwesomeIcon icon={faExternalLinkAlt} />
                                    </MDBDropdownLink>
                                </MDBDropdownItem>
                            )
                            : null
                    }
                    {
                        sticker
                            ? (
                                <MDBDropdownItem>
                                    <MDBDropdownLink href={sticker}>
                                        {t(`Sticker`)}
                                    </MDBDropdownLink>
                                </MDBDropdownItem>
                            )
                            : null
                    }
                </MDBDropdownMenu>
            </MDBDropdown>
        )
        : null
}

const VideosDropdown = ({ video = null, type, sub_type }) => {
    const { t } = useTranslation()
    const videoTypes = [2, 10]
    const isGifVideo = parseInt(type) === 2 && parseInt(sub_type) === 1
    const mp4Video = video?.urls?.mp4
    const mp4VideoCaptionFree = (mp4Video || '').replace(/\.mp4$/, '-clean.mp4')
    const gifVideo = video?.urls?.gif && isGifVideo
    const shouldRender = (mp4Video || gifVideo) && videoTypes.includes(parseInt(type))

    return shouldRender
        ? (
            <MDBDropdown>
                <DropdownToggle size="sm">
                    {t(`Videos`)}
                </DropdownToggle>
                <MDBDropdownMenu>
                    {
                        gifVideo
                            ? (
                                <MDBDropdownItem>
                                    <MDBDropdownLink href={gifVideo}>
                                        {t(`GIF Video`)}
                                    </MDBDropdownLink>
                                </MDBDropdownItem>
                            )
                            : (
                                <>
                                    <MDBDropdownItem>
                                        <MDBDropdownLink href={mp4Video}>
                                            {t(`Watermarked with Captions`)}
                                        </MDBDropdownLink>
                                    </MDBDropdownItem>
                                    <MDBDropdownItem>
                                        <MDBDropdownLink href={mp4VideoCaptionFree}>
                                            {t(`Watermarked Caption-free`)}
                                        </MDBDropdownLink>
                                    </MDBDropdownItem>
                                </>
                            )
                    }
                </MDBDropdownMenu>
            </MDBDropdown>
        )
        : null
}

const AudiosDropdown = ({ audio = '' }) => {
    const { t } = useTranslation()
    const shouldRender = audio

    return shouldRender
        ? (
            <MDBDropdown>
                <DropdownToggle size="sm">
                    {t(`Audios`)}
                </DropdownToggle>
                <MDBDropdownMenu>
                    {
                        audio
                            ? (
                                <MDBDropdownItem>
                                    <MDBDropdownLink href={audio}>
                                        {t(`Extracted`)}
                                    </MDBDropdownLink>
                                </MDBDropdownItem>
                            )
                            : null
                    }
                </MDBDropdownMenu>
            </MDBDropdown>
        )
        : null
}

const AnswerActions = (props) => {
    const { t } = useTranslation()
    const {
        likes,
        comments,
        created_at,
        duplicate,
        late,
        id,
        photo,
        video,
        audio,
        type,
        sub_type,
        sticker,
        regenerateState,
    } = props

    const { game_id } = useParams()
    const isLate = late === 2
    const isEarly = late === 3
    const isDuplicate = !!duplicate
    const hasWarning = isLate || isEarly || isDuplicate
    const { regenerate, isRegenerating, onRegenerate } = regenerateState

    return (
        <>
            <ActionSpace>
                <ActionSpace direction="horizontal" className="mb-2 border-top border-bottom py-2" block>
                    <ActionSpace direction="horizontal">
                        <FontAwesomeIcon icon={faThumbsUp} />
                        {likes ?? 0} {t(`likes`)}
                    </ActionSpace>
                    <ActionSpace direction="horizontal">
                        <FontAwesomeIcon icon={faComment} />
                        {comments ?? 0} {t(`comments`)}
                    </ActionSpace>
                    <ActionSpace direction="horizontal">
                        <Timeago.Display time={created_at} />
                    </ActionSpace>
                </ActionSpace>
                {
                    hasWarning
                        ? (
                            <ActionSpace direction="horizontal" justify="flex-start" className="mb-2">
                                {
                                    isLate
                                        ? (
                                            <MDBBadge color="danger" light>
                                                <ActionSpace direction="horizontal">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {t(`Late Submission!`)}
                                                </ActionSpace>
                                            </MDBBadge>
                                        )
                                        : null
                                }
                                {
                                    isEarly
                                        ? (
                                            <MDBBadge color="danger" light>
                                                <ActionSpace direction="horizontal">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {t(`Early Submission!`)}
                                                </ActionSpace>
                                            </MDBBadge>
                                        )
                                        : null
                                }
                                {
                                    isDuplicate
                                        ? (
                                            <MDBBadge color="warning" light>
                                                <ActionSpace direction="horizontal">
                                                    <FontAwesomeIcon icon={faClone} />
                                                    {t(`Duplicate Submission!`)}
                                                </ActionSpace>
                                            </MDBBadge>
                                        )
                                        : null
                                }
                            </ActionSpace>
                        )
                        : null
                }
                <ActionSpace direction="horizontal" justify="flex-start">
                    <LinkButton 
                        title={t("Share directly via this URL.")}
                        to={`/answers/show/${game_id}/${id}`}
                        className="btn--flat" 
                        size="sm"
                        outline
                    >
                        {t(`Page`)}
                    </LinkButton>
                    <ImagesDropdown 
                        photo={photo} 
                        sticker={sticker}
                        type={type}
                    />
                    <VideosDropdown
                        video={video}
                        type={type}
                        sub_type={sub_type}
                    />
                    <AudiosDropdown
                        audio={audio}
                    />
                    {
                    regenerate
                        ? (
                            <Button
                                onClick={onRegenerate}
                                title={t("Attempt to regenerate the media for this answer.")}
                                size="sm"
                            >
                                {t('Regenerate')} { isRegenerating ? <Spinner size="0.875rem" /> : null }
                            </Button>
                        )
                        : null
                }
                </ActionSpace>
            </ActionSpace>
        </>
    )
}

export default AnswerActions